import { formatPhone, parseDateValue } from '@/lib/utils';
import type { Lead } from '@/services/leads.service';

/**
 * Builds the "lead details" header that gets prepended to the lead message
 * whenever the lead's content is copied somewhere else (intake notes on
 * convert / attach-as-plan, comm-log notes on merge).
 *
 * Without this the copied content is just `contacts.message` — the demographic
 * and source fields shown at the top of LeadDetailModal (name, email, address,
 * DOB, lead source, custom form answers) never travel with it. Mirrors the
 * field order of LeadDetailView's Contact Details / Additional Info / Source
 * Info sections.
 *
 * Output is HTML, not plain text: the intake notes are stored into
 * `comm_log.notes` (LeadIntakeController::store wraps them in
 * 'Plan Created<br /><br />') and that column is rendered as HTML, so newline
 * separators would collapse the whole header onto one line. Markup is kept
 * identical to `LeadsController::buildLeadDetailsHeaderHtml()` so all three
 * flows render the same. Rows are newline-separated so the block stays legible
 * in the intake Notes textarea, where the user can still edit it — HTML
 * ignores those newlines when rendering.
 *
 * Returns '' when the lead carries no detail fields worth prepending.
 */
export function buildLeadDetailsHeaderHtml(lead: Lead): string {
  const sections: string[] = [];

  const contact = rows([
    ['Name', joinName(lead)],
    ['Date of Birth', formatLongDate(lead.birthDate)],
    ['Phone', formatPhone(lead.cellPhone || lead.phone || '') || null],
    ['Email', lead.email],
    ['Address', lead.address],
    ['City', lead.city],
    ['State', lead.state],
    ['ZIP', lead.zip && String(lead.zip) !== '0' ? String(lead.zip) : null],
  ]);
  if (contact) sections.push(section('Contact Details', contact));

  const additional = rows(
    (lead.contactsRecord ?? []).map((row) => [row.label, row.value] as const),
  );
  if (additional) sections.push(section('Additional Info', additional));

  const source = rows([
    ['Subject', lead.subject],
    ['Practice Referral', lead.practiceReferral],
    ['Lead Source', lead.leadSourceName],
    ['Originating Site', lead.siteReferrer],
    ['Referring URL', lead.referUrl],
    ['Search Terms', lead.referKeywords],
    ['Received', formatLongDate(lead.dateAdded)],
  ]);
  if (source) sections.push(section('Source Info', source));

  if (sections.length === 0) return '';

  return [
    '<div style="font-family:Arial,Helvetica,sans-serif;font-size:13px;line-height:1.5">',
    `<div style="font-weight:bold">Lead #${lead.leadId}</div>`,
    ...sections,
    '</div>',
    '<hr />',
  ].join('\n');
}

/**
 * Prepend `buildLeadDetailsHeaderHtml(lead)` to the lead's copied content. Safe
 * to call with empty content — the header is still returned on its own.
 */
export function prependLeadDetails(lead: Lead, content: string): string {
  const header = buildLeadDetailsHeaderHtml(lead);
  if (!header) return content;
  const body = content.trim();
  if (!body) return header;
  return `${header}\n${body}`;
}

function joinName(lead: Lead): string | null {
  const name = [lead.firstName, lead.middleName, lead.lastName]
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .join(' ');
  return name || null;
}

/** Render "Label: value" rows, dropping blanks. Labels and values are escaped. */
function rows(pairs: ReadonlyArray<readonly [string, string | null | undefined]>): string {
  return pairs
    .filter(([label, value]) => {
      if (label.trim() === '') return false;
      const trimmed = (value ?? '').trim();
      return trimmed !== '' && trimmed !== '—';
    })
    .map(
      ([label, value]) =>
        `<div><span style="color:#64748b">${escapeHtml(label)}:</span> ${escapeHtml((value ?? '').trim())}</div>`,
    )
    .join('\n');
}

function section(title: string, body: string): string {
  return [
    '<div style="margin-top:8px">',
    `<div style="font-weight:bold">${escapeHtml(title)}</div>`,
    body,
    '</div>',
  ].join('\n');
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * `parseDateValue` (not a bare `new Date(value)`) because birthDate arrives as
 * 'YYYY-MM-DD': the spec parses a date-only ISO string as UTC midnight, so
 * rendering it in a US locale rolled the day back one (a 8/3 DOB printed here as
 * August 2 while the modal's `formatDate` — same parser — printed August 3).
 * It also drops v4's '0000-00-00' sentinel.
 */
function formatLongDate(value: string | null | undefined): string | null {
  const date = parseDateValue(value);
  if (!date) return null;
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}
